import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

export default function SavedSearches() {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [savedSearches, setSavedSearches] = useState([])
  const [searchFilter, setSearchFilter] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  useEffect(() => {
    checkAuth()
  }, [])

  async function checkAuth() {
    try {
      const { data: { session } } = await supabase.auth.getSession()

      if (!session) {
        navigate('/login', { replace: true })
        return
      }

      setUser(session.user)
      await loadUserProfile(session.user.id)
      await loadSavedSearches(session.user.id)
      setLoading(false)
    } catch (error) {
      console.error('Auth error:', error)
      navigate('/login', { replace: true })
    }
  }

  async function loadUserProfile(userId) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle()

    if (!error && data) {
      setUserProfile(data)
    }
  }

  async function loadSavedSearches(userId) {
    const { data, error } = await supabase
      .from('saved_searches')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error loading saved searches:', error)
    } else {
      setSavedSearches(data || [])
    }
  }

  async function handleDelete(id) {
    const { error } = await supabase
      .from('saved_searches')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Error deleting search:', error)
    } else {
      setSavedSearches(savedSearches.filter(s => s.id !== id))
      setDeleteConfirm(null)
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut()
    navigate('/')
  }

  const filteredSearches = savedSearches.filter(search =>
    search.search_name.toLowerCase().includes(searchFilter.toLowerCase()) ||
    search.job_text.toLowerCase().includes(searchFilter.toLowerCase())
  )

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>
  }

  return (
    <div style={{ minHeight: '100vh' }}>
      <div className="nav">
        <div className="nav-content">
          <div
            className="nav-title"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}
          >
            <span>&gt; AgencySniper</span>
          </div>
          <div className="nav-actions">
            <button onClick={() => navigate('/dashboard')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Dashboard
            </button>
            <button onClick={() => navigate('/agencies')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Agencies
            </button>
            <button onClick={() => navigate('/recent-searches')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Recent
            </button>
            {userProfile?.role === 'admin' && (
              <button onClick={() => navigate('/admin')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
                Admin
              </button>
            )}
            <button onClick={handleSignOut} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="card">
          <h2 style={{ fontSize: '32px', marginBottom: '16px', fontWeight: '700', color: '#98FB98' }}>
            &gt; Saved Operations
          </h2>
          <p style={{ color: 'rgba(152, 251, 152, 0.85)', marginBottom: '28px', fontSize: '15px' }}>
            Your archived job analysis operations
          </p>

          <input
            type="text"
            className="input"
            placeholder="Filter saved searches..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            style={{ marginBottom: '24px' }}
          />

          {filteredSearches.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'rgba(152, 251, 152, 0.65)' }}>
              <p style={{ fontSize: '18px', marginBottom: '12px' }}>No saved searches found</p>
              <p style={{ fontSize: '14px' }}>
                {searchFilter ? 'Try adjusting your filter' : 'Save your first analysis from the dashboard'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '20px' }}>
              {filteredSearches.map((search) => {
                const topCompany = search.result_json?.companies?.[0]

                return (
                  <div
                    key={search.id}
                    className="result-item"
                    style={{ position: 'relative' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                      <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#f5f5f5', margin: 0 }}>
                        {search.search_name}
                      </h3>
                      <span style={{ fontSize: '12px', color: 'rgba(200, 200, 200, 0.8)' }}>
                        {new Date(search.created_at).toLocaleDateString()}
                      </span>
                    </div>

                    <p style={{ color: '#d0d0d0', fontSize: '14px', marginBottom: '16px', lineHeight: '1.6' }}>
                      {search.job_text.substring(0, 200)}...
                    </p>

                    {topCompany && (
                      <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(40, 40, 40, 0.6)', borderRadius: '4px', border: '1px solid rgba(152, 251, 152, 0.3)' }}>
                        <div style={{ fontSize: '13px', color: 'rgba(200, 200, 200, 0.8)', marginBottom: '6px' }}>
                          Top Match:
                        </div>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#f5f5f5' }}>
                          {topCompany.name}
                        </div>
                        <div style={{ fontSize: '13px', color: '#d0d0d0', marginTop: '6px' }}>
                          Confidence: {topCompany.confidence}%
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        onClick={() => {
                          setDeleteConfirm(deleteConfirm === search.id ? null : search.id)
                        }}
                        className="btn-secondary"
                        style={{ padding: '8px 16px', fontSize: '13px', flex: 1 }}
                      >
                        {deleteConfirm === search.id ? 'Cancel' : 'Delete'}
                      </button>

                      {deleteConfirm === search.id && (
                        <button
                          onClick={() => handleDelete(search.id)}
                          className="btn"
                          style={{ padding: '8px 16px', fontSize: '13px', flex: 1, background: 'rgba(100, 0, 0, 0.8)', borderColor: '#FF3333' }}
                        >
                          Confirm Delete
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
